import { useLayoutEffect, useMemo, useRef, useState } from "react";


const useCheckQrRegenerateConfirmation = ({ eventID, qrUrl, disabled, onGenerate }) => {
    const context = useMemo(() => ({ eventID, qrUrl }), [ eventID, qrUrl ]);
    const activeContextRef = useRef(context);
    const disabledRef = useRef(disabled);
    const mountedRef = useRef(true);
    const requestRef = useRef(null);
    const targetRef = useRef(null);
    const [ target, setTarget ] = useState(null);
    const [ request, setRequest ] = useState(null);

    activeContextRef.current = context;
    disabledRef.current = disabled;

    useLayoutEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            requestRef.current = null;
            targetRef.current = null;
        };
    }, []);

    useLayoutEffect(() => {
        targetRef.current = null;
        setTarget(null);
    }, [ context ]);

    const isCurrent = (candidate) => (
        mountedRef.current && candidate === activeContextRef.current
    );

    const generate = async (candidate) => {
        if (!isCurrent(candidate) || disabledRef.current || !eventID) return null;
        if (requestRef.current?.context === candidate) return null;

        const currentRequest = { context: candidate };
        requestRef.current = currentRequest;
        targetRef.current = null;
        setRequest(currentRequest);

        try
        {
            return await onGenerate();
        } catch (error)
        {
            // Generation callers present mutation errors; keep confirmation event handlers safe.
            return null;
        } finally
        {
            if (requestRef.current === currentRequest)
            {
                requestRef.current = null;
                if (mountedRef.current) setRequest(null);
            }
            if (isCurrent(candidate)) setTarget(null);
        }
    };

    const requestGeneration = () => {
        if (!isCurrent(context) || disabledRef.current || requestRef.current?.context === context) return;

        if (qrUrl)
        {
            targetRef.current = context;
            setTarget(context);
            return;
        }

        return generate(context);
    };

    const pending = request?.context === context;

    return {
        requestGeneration,
        confirmationProps: {
            visible: target === context,
            pending,
            disabled: Boolean(disabled),
            onConfirm: () => {
                if (target === context && targetRef.current === context) return generate(target);
            },
            onCancel: () => {
                if (isCurrent(context) && requestRef.current?.context !== context)
                {
                    targetRef.current = null;
                    setTarget(null);
                }
            },
        },
    };
};

export default useCheckQrRegenerateConfirmation;
